import clsx from "clsx";
import React, { useEffect, useRef } from "react";
import toggleBodyScrolling from "~/utils/toggleBodyScrolling";
import { useClickOutside } from "~/utils/useClickOutside";
import CallToAction from "../CallToAction";
import CustomImage from "../CustomImage";
import { type LocationData } from "~/slices/LocationsSection";
import { type Continent } from "./MapWrapper";

export default function LocationDrawer({
  locations,
  activeContinent,
  resetActiveLocation,
  activeLocationName,
}: {
  locations: LocationData[];
  activeContinent: Continent;
  resetActiveLocation: () => void;
  activeLocationName?: string;
}) {
  const container = useRef<HTMLDivElement>(null);

  // Filter out current continent
  const currentContinent =
    (activeLocationName &&
      locations.filter((i) => i.name == activeContinent)[0]) ??
    null;

  // filter out current location
  const currentLocation =
    (activeLocationName &&
      locations.find((location) => location.name == activeLocationName)) ??
    null;

  // If clicked outside of sidedrawer, close the sidedrawer
  useClickOutside(container, () => {
    if (activeLocationName) resetActiveLocation();
  });

  useEffect(() => {
    if (activeLocationName) {
      window.addEventListener("keydown", checkForEscapeButton);
      toggleBodyScrolling(!!activeLocationName);
    } else {
      window.removeEventListener("keydown", checkForEscapeButton);
    }

    return () => {
      window.removeEventListener("keydown", checkForEscapeButton);
    };
  }, [activeLocationName, activeContinent]);

  function checkForEscapeButton(e: KeyboardEvent) {
    if (e.key === "Escape") {
      resetActiveLocation();
    }
  }

  return (
    <div
      className={clsx([
        "fixed right-0 top-0 z-50 flex h-full w-full justify-end bg-black/50 transition-all duration-scalingCursor ",
        activeLocationName
          ? "pointer-events-auto opacity-100 "
          : "pointer-events-none opacity-0",
      ])}
    >
      <button
        onClick={() => resetActiveLocation()}
        className="absolute right-8 top-8 z-10 rotate-0 transition-transform hover:rotate-90"
        aria-label="Close popup"
      >
        <div className="relative flex h-4 h-full w-4 w-full justify-between">
          <span className="absolute left-1/2 top-1/2 h-px w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-black"></span>
          <span className="absolute left-1/2 top-1/2 h-px w-4 -translate-x-1/2 -translate-y-1/2 -rotate-45 bg-black"></span>
        </div>
      </button>
      {currentLocation && (
        <div
          ref={container}
          className={clsx([
            "flex h-full flex-col overflow-y-scroll bg-grey drop-shadow-2xl transition-all duration-scalingCursor md:w-120",
            activeLocationName
              ? "pointer-events-auto opacity-100"
              : "pointer-events-none opacity-0",
          ])}
        >
          {currentLocation?.preview ? (
            <CustomImage
              data={currentLocation.preview}
              className="h-72 w-full object-cover"
              sizes="(min-width: 756px) 495px, 426px"
            />
          ) : null}

          <div className="flex flex-col gap-14 px-15 py-16">
            <h3 className="flex flex-col text-heading-5">
              <span className="text-heading-6 opacity-60">
                {currentLocation?.country ?? null}
              </span>
              {currentLocation.name ?? null}
            </h3>

            <div className="my-2 flex flex-col gap-10">
              {currentLocation.statistics &&
                currentLocation.statistics.length > 0 &&
                currentLocation.statistics.map((statistic, index) => (
                  // Since the first element should beb igger (the number), the first linebreak should be a h3 and the second one should be a h4 in prismic
                  <div className="flex flex-col items-start gap-1" key={index}>
                    <span className={clsx("text-heading-6", "text-black")}>
                      {statistic?.number}
                    </span>
                    <p className="text-small-body text-black/80">
                      {statistic?.title}
                    </p>
                  </div>
                ))}
            </div>

            <span className="my-4 w-44 flex-shrink-0">
              {currentLocation?.link && (
                <button onClick={() => toggleBodyScrolling(false)}>
                  <CallToAction to={currentLocation.link} isFilled>
                    See Location
                  </CallToAction>
                </button>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
